import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../userContext';
import { 
  collection, query, where, getDocs, doc, updateDoc, arrayUnion,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { 
  FaTimes, FaVideo, FaFile, FaImage, FaLink,
  FaCalendarAlt
} from 'react-icons/fa';
import './PopupAvisos.css';

function PopupAvisos() {
  const { user } = useContext(UserContext);
  const [avisoAtual, setAvisoAtual] = useState(null);
  const [filaAvisos, setFilaAvisos] = useState([]);
  const [indiceAtual, setIndiceAtual] = useState(0);

  useEffect(() => {
    if (user?.uid) {
      carregarAvisosPopup();
    }
  }, [user]);

  const carregarAvisosPopup = async () => {
    if (!user?.uid) return;

    try {
      const agora = Timestamp.now();
      
      // Buscar avisos configurados para popup, ativos no período atual
      const q = query(
        collection(db, 'avisos'),
        where('ativo', '==', true),
        where('showPopup', '==', true),
        where('dataInicio', '<=', agora),
        where('dataFim', '>=', agora)
      );
      
      const snapshot = await getDocs(q);
      const avisosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dataInicio: doc.data().dataInicio?.toDate?.() || new Date(),
        dataFim: doc.data().dataFim?.toDate?.() || new Date(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      // Filtrar avisos que o usuário ainda não visualizou
      const avisosNaoVistos = avisosData.filter(aviso => {
        const visualizacoes = aviso.visualizacoes || [];
        return !visualizacoes.includes(user.uid);
      });

      if (avisosNaoVistos.length > 0) {
        // Ordenar por data de criação (mais recentes primeiro)
        avisosNaoVistos.sort((a, b) => b.createdAt - a.createdAt);
        setFilaAvisos(avisosNaoVistos);
        setAvisoAtual(avisosNaoVistos[0]);
        setIndiceAtual(0);
      }
    } catch (error) {
      console.error('Erro ao carregar avisos popup:', error);
    }
  };

  const marcarComoVisto = async (avisoId) => {
    if (!user?.uid) return;

    try {
      await updateDoc(doc(db, 'avisos', avisoId), {
        visualizacoes: arrayUnion(user.uid)
      });
    } catch (error) {
      console.error('Erro ao marcar como visto:', error);
    }
  };

  const fecharPopup = async () => {
    if (avisoAtual) {
      await marcarComoVisto(avisoAtual.id);
    }
    
    // Mostrar próximo aviso se existir
    const proximoIndice = indiceAtual + 1;
    if (proximoIndice < filaAvisos.length) {
      setIndiceAtual(proximoIndice);
      setAvisoAtual(filaAvisos[proximoIndice]);
    } else {
      // Não há mais avisos
      setAvisoAtual(null);
      setFilaAvisos([]);
      setIndiceAtual(0);
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'video':
      case 'video_texto':
        return <FaVideo className="tipo-icon video" />;
      case 'texto_imagem':
        return <FaImage className="tipo-icon imagem" />;
      case 'texto_link':
        return <FaLink className="tipo-icon link" />;
      default:
        return <FaFile className="tipo-icon texto" />;
    }
  };

  const getGoogleDriveFileId = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, '');
      if (host !== 'drive.google.com') return null;

      const pathParts = parsed.pathname.split('/').filter(Boolean);
      const fileIndex = pathParts.findIndex((part) => part === 'd');
      if (fileIndex > -1 && pathParts[fileIndex + 1]) {
        return pathParts[fileIndex + 1];
      }

      return parsed.searchParams.get('id');
    } catch (_) {
      return null;
    }
  };

  const normalizeImageUrl = (url) => {
    const cleanUrl = (url || '').trim();
    const driveId = getGoogleDriveFileId(cleanUrl);
    if (!driveId) return cleanUrl;
    return `https://drive.google.com/thumbnail?id=${driveId}&sz=w2000`;
  };

  const getImageFallbackUrl = (url) => {
    const driveId = getGoogleDriveFileId((url || '').trim());
    if (!driveId) return '';
    return `https://drive.google.com/uc?export=view&id=${driveId}`;
  };

  const normalizeVideoUrl = (url) => {
    const cleanUrl = (url || '').trim();
    const driveId = getGoogleDriveFileId(cleanUrl);
    if (!driveId) return cleanUrl;
    return `https://drive.google.com/uc?export=download&id=${driveId}`;
  };

  const renderConteudoAviso = (aviso) => {
    const { tipo, conteudo, imagemUrl, imagemURL, imageUrl, linkUrl, videoUrl } = aviso;
    const imagemOriginal = (imagemUrl || imagemURL || imageUrl || '').trim();
    const imagemAviso = normalizeImageUrl(imagemOriginal);
    const imagemFallback = getImageFallbackUrl(imagemOriginal);
    const videoMediaUrl = normalizeVideoUrl(videoUrl);
    const isYouTubeUrl = !!videoMediaUrl && (
      videoMediaUrl.includes('youtube.com') ||
      videoMediaUrl.includes('youtu.be') ||
      videoMediaUrl.includes('youtube-nocookie.com')
    );
    const embedUrl = isYouTubeUrl ? getYouTubeEmbedUrl(videoMediaUrl) : null;

    return (
      <div className="popup-conteudo-completo">
        {!!conteudo && <p className="popup-texto">{conteudo}</p>}
        
        {imagemAviso && (
          <div className="popup-media">
            <img 
              src={imagemAviso} 
              alt="Imagem do aviso"
              className="popup-imagem"
              onError={(e) => {
                if (imagemFallback && e.currentTarget.src !== imagemFallback) {
                  e.currentTarget.src = imagemFallback;
                  return;
                }
                e.currentTarget.style.display = 'none';
              }}
            />
            <a
              href={imagemOriginal || imagemAviso}
              target="_blank"
              rel="noopener noreferrer"
              className="popup-link"
              style={{ marginTop: '1rem' }}
            >
              <FaLink />
              Abrir imagem
            </a>
          </div>
        )}
        
        {tipo === 'texto_link' && linkUrl && (
          <div className="popup-media">
            <a 
              href={linkUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="popup-link"
            >
              <FaLink />
              Acessar Link
            </a>
          </div>
        )}
        
        {(tipo === 'video' || tipo === 'video_texto') && videoMediaUrl && (
          <div className="popup-media">
            {embedUrl ? (
              <div className="popup-video-wrapper">
                <iframe
                  src={embedUrl}
                  title="Vídeo do aviso"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="popup-video"
                />
              </div>
            ) : isYouTubeUrl ? (
              <a
                href={videoMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="popup-link"
              >
                <FaLink />
                Abrir vídeo no YouTube
              </a>
            ) : (
              <video controls className="popup-video">
                <source src={videoMediaUrl} type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </video>
            )}
          </div>
        )}
      </div>
    );
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, '');

      if (host === 'youtu.be') {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        return id?.length === 11 ? id : null;
      }

      if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
        const watchId = parsed.searchParams.get('v');
        if (watchId?.length === 11) return watchId;

        const pathParts = parsed.pathname.split('/').filter(Boolean);
        const embedIndex = pathParts.findIndex((part) => part === 'embed' || part === 'shorts' || part === 'live');
        if (embedIndex >= 0 && pathParts[embedIndex + 1]?.length === 11) {
          return pathParts[embedIndex + 1];
        }
      }
    } catch (_) {
      return null;
    }

    return null;
  };

  if (!avisoAtual) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-modal">
        {/* Indicador de posição se houver múltiplos avisos */}
        {filaAvisos.length > 1 && (
          <div className="popup-indicador">
            {indiceAtual + 1} de {filaAvisos.length}
          </div>
        )}

        <div className="popup-header">
          <div className="popup-header-left">
            {getTipoIcon(avisoAtual.tipo)}
            <div className="popup-titulo-data">
              <h3 className="popup-titulo">{avisoAtual.titulo}</h3>
              <div className="popup-data">
                <FaCalendarAlt />
                <span>
                  {avisoAtual.createdAt.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
          
          <button onClick={fecharPopup} className="popup-close">
            <FaTimes />
          </button>
        </div>

        <div className="popup-content">
          {renderConteudoAviso(avisoAtual)}
        </div>

        <div className="popup-footer">
          <button onClick={fecharPopup} className="popup-button">
            {filaAvisos.length > 1 && indiceAtual < filaAvisos.length - 1 
              ? 'Próximo' 
              : 'Entendi'
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default PopupAvisos;
